import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { decryptSecret, type DecryptInput } from '../lib/vaultCrypto';

interface PayloadResp extends DecryptInput {
  has_pin: boolean;
  views_remaining: number;
  max_views: number;
  expires_at: string;
  burn_after_read: boolean;
}

export function VaultViewPage() {
  const { token } = useParams<{ token: string }>();
  const [payload, setPayload] = useState<PayloadResp | null>(null);
  const [passphrase, setPassphrase] = useState(() => decodeURIComponent(window.location.hash.slice(1)));
  const [plaintext, setPlaintext] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchedRef = useRef(false);
  useEffect(() => {
    if (!token || fetchedRef.current) return;
    fetchedRef.current = true; // guard StrictMode double-mount (sinon le burn se déclenche 2x)
    setLoading(true);
    fetch(`/api/vault/secrets/${token}`)
      .then(async (r) => {
        if (r.status === 410) {
          const b = (await r.json().catch(() => ({}))) as { error?: string };
          throw new Error(b.error || 'Secret expiré ou consommé');
        }
        if (r.status === 404) throw new Error('Secret introuvable');
        if (!r.ok) throw new Error(`Erreur ${r.status}`);
        return r.json();
      })
      .then((d: PayloadResp) => setPayload(d))
      .catch((e) => setErr(e instanceof Error ? e.message : 'Erreur'))
      .finally(() => setLoading(false));
  }, [token]);

  // Auto-déchiffrement si la passphrase est dans le fragment URL
  useEffect(() => {
    if (payload && passphrase && !plaintext) {
      void tryDecrypt();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  const tryDecrypt = async () => {
    if (!payload) return;
    setErr(null);
    try {
      const pt = await decryptSecret(payload, passphrase);
      setPlaintext(pt);
      // Wipe le fragment URL une fois déchiffré (anti-shoulder-surf)
      if (window.location.hash) {
        history.replaceState(null, '', window.location.pathname);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Déchiffrement impossible');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-soft)', padding: '40px 20px' }}>
      <div style={{ maxWidth: 600, margin: '0 auto', background: 'var(--bg)', borderRadius: 12, padding: 32, boxShadow: 'var(--shadow)' }}>
        <h1 style={{ fontSize: 'var(--fs-2xl)', fontWeight: 700, color: 'var(--accent)', marginTop: 0 }}>
          🔐 Secret partagé
        </h1>

        {loading && <div style={{ color: 'var(--text-mute)' }}>Chargement…</div>}

        {err && !plaintext && (
          <div
            style={{
              background: 'var(--danger-soft)',
              border: '1px solid var(--danger-border)',
              borderRadius: 8,
              padding: 14,
              color: 'var(--danger)',
              fontSize: 'var(--fs-sm)',
            }}
          >
            {err}
          </div>
        )}

        {payload && !plaintext && (
          <div style={{ marginTop: 12 }}>
            <p style={{ color: 'var(--text-mute)', fontSize: 'var(--fs-sm)' }}>
              Le contenu est chiffré dans votre navigateur. Saisissez la passphrase pour le déchiffrer.
            </p>
            <input
              autoFocus
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && tryDecrypt()}
              placeholder="Passphrase"
              style={{
                width: '100%',
                padding: 10,
                border: '1px solid var(--border-strong)',
                borderRadius: 6,
                fontSize: 'var(--fs-base)',
                fontFamily: 'ui-monospace, Menlo, monospace',
                marginTop: 4,
              }}
            />
            <button className="btn primary" onClick={tryDecrypt} disabled={!passphrase} style={{ marginTop: 12 }}>
              🔓 Déchiffrer
            </button>
            <div style={{ marginTop: 16, fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>
              Vues restantes : {payload.views_remaining + 1} sur {payload.max_views}
              {payload.burn_after_read && ' · Détruit après dernière lecture'}
              <br />
              Expire : {new Date(payload.expires_at).toLocaleString('fr-FR')}
            </div>
          </div>
        )}

        {plaintext && (
          <div>
            <div style={{ color: 'var(--success)', fontSize: 'var(--fs-sm)', marginBottom: 8 }}>✓ Secret déchiffré</div>
            <pre
              style={{
                background: 'var(--bg-soft)',
                padding: 14,
                borderRadius: 6,
                fontFamily: 'ui-monospace, Menlo, monospace',
                fontSize: 'var(--fs-sm)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                margin: 0,
              }}
            >
              {plaintext}
            </pre>
            <button
              className="btn primary"
              onClick={() => navigator.clipboard.writeText(plaintext)}
              style={{ marginTop: 12 }}
            >
              Copier
            </button>
            {payload?.burn_after_read && (
              <div style={{ marginTop: 16, fontSize: 'var(--fs-xs)', color: 'var(--warning)' }}>
                ⚠ Ce secret vient d&apos;être consommé et a été détruit côté serveur. Recopiez-le maintenant si nécessaire.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
